import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChildren,
} from '@angular/core';
import { NgIf, NgClass, NgFor, NgSwitch, NgSwitchCase, NgSwitchDefault, AsyncPipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import {
	CallToActionResponse,
	CardComponent,
	CtaComponent,
	TrackImpressionDirective,
	WappleLoadingComponent,
	DatalayerService,
	getProductType,
	ListType,
	ModalRoutingService,
	ProductImpressionData,
	ProductType,
	ShopperService,
	ShopperState,
	SearchTarget,
	ViewItemListArgs,
	ProductImpressionAndViewItemListArgs,
	LoggingService,
	FeatureService,
	TealiumEventEnum,
	PromoCPPImpressionArgs,
	AppSettingsService,
	getDiscountDiffValue,
	WINDOW,
	CustomWindow,
} from '@woolworthsnz/styleguide';
import {
	ContextResponse,
	FilterCriteria,
	ProductResponse,
	ProductTag,
	SortOptionItem,
	TrolleyItemResponse,
} from '@woolworthsnz/trader-api';
import { distinctUntilChanged } from 'rxjs/operators';
import { isProductOutOfStock } from '../../helpers/product-availability.helper';
import { PromotionalCarouselResponseModel } from '../../models/PromotionalCarouselResponseModel';
import { CitrusAdService } from '../../services/citrus-ad.service';
import { LayoutType } from '../../models/layout-type.model';
import { StampComponent } from '../stamp/stamp.component';
import { ProductImpression } from '../../models/tealium-events';
import { StampListComponent } from '../stamp-list/stamp-list.component';
import { StampGridComponent } from '../stamp-grid/stamp-grid.component';
import { ContentInGridComponent } from '../content-in-grid/content-in-grid.component';
import { TealiumUtagService } from '@woolworthsnz/analytics';
import { Observable, of } from 'rxjs';
import { BoostsFacade } from '@woolworthsnz/everyday-rewards';
import { extractQLCodeFromCtaName } from '../../helpers/product-response.helper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ViewItemParams } from '../../models/view-item-params';
import {
	VIEW_ITEM_DATA_NOT_APPLICABLE,
	VIEW_ITEM_ONLINE_SAMPLE_BRAND,
	VIEW_ITEM_ONLINE_SAMPLE_LIST_NAME,
} from '../../constants/view-item-constants';

@Component({
	selector: 'product-grid',
	templateUrl: './grid.component.html',
	styleUrls: ['./grid.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [],
	standalone: true,
	imports: [
		NgIf,
		NgClass,
		WappleLoadingComponent,
		NgFor,
		NgSwitch,
		NgSwitchCase,
		AsyncPipe,
		CtaComponent,
		ContentInGridComponent,
		TrackImpressionDirective,
		NgSwitchDefault,
		CardComponent,
		StampGridComponent,
		StampListComponent,
	],
})
export class GridComponent implements OnInit, OnDestroy {
	@ViewChildren(StampComponent) stamps: StampComponent[];

	@HostBinding('attr.data-loading')
	@Input()
	loading = true;
	@Input() department: string;
	@Input() aisle: string;
	@Input() shelf: string;
	@Input() isExistingSkuWhenChangingOrder = false;
	@Input() target: string;
	@Input() productIndexOffset = 0;
	@Input() cppItemCount?: number;
	@Input() maxBoughtBeforePosition = 0;
	@Input() selectedFilters: FilterCriteria[];
	@Input() searchSortOptions: SortOptionItem[];
	@Input() baseSortOptions: string;
	@Input() hideUnavailableProductPrice: boolean;

	@HostBinding('attr.columns-extended')
	@Input()
	shouldColumnsExtended = false;

	// The following are all lower case because they are accessed by webcomponents
	@Input() canremove = false;
	@Input() emptymessage: string;
	@Input() listid: string;
	@Input() listName: string;
	@Input() listtype: ListType;
	@Input() title: string;
	@Input() gridIndex: number;
	@Input()
	get name(): string | undefined {
		return this._name;
	}
	set name(val: string | undefined) {
		this._name = val;
		if (val) {
			this.productImpressionArgs = this.getProductImpressionArgs();
		}
	}
	@Input()
	set contentInGridCarouselSuffix(suffix: number) {
		this._contentInGridCarouselSuffix = suffix;
	}

	private _name?: string;
	private creativeName = `${this._searchQuery !== undefined ? 'Search' : 'Browse'} Product`;

	private _searchQuery?: string;
	@Input() set searchQuery(query: string) {
		if (query) {
			this._searchQuery = query;
			this.productImpressionArgs = this.getProductImpressionArgs();
		}
	}

	private _totalItemsCount?: number;
	@Input() set totalItemsCount(count: number) {
		if (count) {
			this._totalItemsCount = count;
			this.productImpressionArgs = this.getProductImpressionArgs();
		}
	}

	@HostBinding('attr.data-layout')
	@Input()
	layout: LayoutType = LayoutType.grid;

	// don't rename them into camelcase, that creates an issue using as a custom element
	@Input() set isshopper(s: boolean | string) {
		if (s && s !== 'false') {
			this._isShopper = true;

			this.shopperService.setState(<ShopperState>{
				isShopper: true,
			});
		}
	}

	@Input() set isloggedin(s: boolean | string) {
		if (s && s !== 'false') {
			this._isLoggedIn = true;

			this.shopperService.setState(<ShopperState>{
				isLoggedIn: true,
			});

			if (this.featureService.isFeatureEnabled(ContextResponse.EnabledFeaturesEnum.RedirectValidation)) {
				this.loggingService.trackEvent('RedirectValidation', {
					shopper: this.shopperService.state?.shopperScvId || 'unknown',
					detail: 'redirect in Grid page',
					isLoggedIn: true,
				});
			}
		}
	}

	@Output() removeProductEmitter: EventEmitter<string> = new EventEmitter();
	@Output() updateAffectedProductsEmitter = new EventEmitter<Map<string, TrolleyItemResponse>>();
	@Output() allowSubstitutesProductEmitter: EventEmitter<string> = new EventEmitter();
	@Output() disallowSubstitutesProductEmitter: EventEmitter<string> = new EventEmitter();

	private _products: (CallToActionResponse | ProductResponse)[];
	private _isShopper = false;
	private _isLoggedIn = false;
	private _productsVisibility = new Map<string, boolean>();
	private _isCartologyGA4EventTrackingEnabled = false;
	private _contentInGridCarouselSuffix = 0;
	private _cartologyGAMCallToAction: CallToActionResponse | undefined;
	shopperState$ = this.shopperService.state$.pipe(
		distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
		takeUntilDestroyed()
	);
	isWebComponent = false;
	productImpressionArgs: ProductImpressionAndViewItemListArgs[] = [];
	gridPositions: number[] = [];
	gridPositionsForBoughtBefore: number[] = [];
	gridCarouselIndex = -1;
	viewItemListArgs: ViewItemListArgs[] = [];
	isCartologyGAMSessionGroup = false;

	constructor(
		@Inject(WINDOW) public window: CustomWindow,
		private cdr: ChangeDetectorRef,
		private shopperService: ShopperService,
		private datalayerService: DatalayerService,
		private citrusAdService: CitrusAdService,
		protected modalRoutingService: ModalRoutingService,
		private loggingService: LoggingService,
		private route: ActivatedRoute,
		private tealiumUtagService: TealiumUtagService,
		private featureService: FeatureService,
		private boostsFacade: BoostsFacade,
		private appSettingsService: AppSettingsService
	) {}

	trackByProductSkuAndSelectedPurchasingUnit = (index: number, product: ProductResponse): string =>
		this.isContentInGridCarousel(product)
			? `${(product as PromotionalCarouselResponseModel)?.id}`
			: this.getTrackingId(product);

	removeProduct(sku: string): void {
		this.removeProductEmitter.emit(sku);
	}

	updateAffectedProducts(affectedProducts: Map<string, TrolleyItemResponse>): void {
		this.updateAffectedProductsEmitter.emit(affectedProducts);
	}

	allowSubstitutesProduct(sku: string): void {
		this.allowSubstitutesProductEmitter.emit(sku);
	}

	disallowSubstitutesProduct(sku: string): void {
		this.disallowSubstitutesProductEmitter.emit(sku);
	}

	showChangedPriceTutorial(productIndex: number): boolean {
		return productIndex === 0 && this.gridIndex === 0;
	}
	get isCPUUIAdjustimentOn(): boolean {
		return this.featureService.isFeatureEnabled(ContextResponse.EnabledFeaturesEnum.EnableCUPUIAdjustments);
	}

	get productsVisibility(): Map<string, boolean> {
		return this._productsVisibility;
	}

	get isShopper(): boolean {
		return this._isShopper;
	}

	get isLoggedIn(): boolean {
		return this._isLoggedIn;
	}

	get isLayoutGrid(): boolean {
		return this.layout === LayoutType.grid;
	}

	@Input() set products(p: (CallToActionResponse | ProductResponse)[] | string) {
		/**
		 * If it's a web component it HAS to be sent in as stringified JSON
		 */
		this.isWebComponent = typeof p === 'string';
		this._products = (this.isWebComponent ? JSON.parse(<string>p) : p) || [];
		if (this._products.length) {
			this.loading = false;

			this.gridCarouselIndex = this._products.findIndex(this.isContentInGridCarousel);
			this.gridPositions = Array.from({ length: this._products.length }, (_, index) =>
				this.gridCarouselIndex > 0 && index >= this.gridCarouselIndex ? index + 2 : index + 1
			);

			this.productImpressionArgs = this.getProductImpressionArgs();
			this.citrusAdService.reportImpressions(this._products);

			let psum = 0;
			this.gridPositionsForBoughtBefore = this._products.map(
				(x) => (psum += x.type !== 'Product' || ('adId' in x && x.adId) ? 0 : 1)
			);
		}

		if (this._searchQuery !== undefined) {
			this._contentInGridCarouselSuffix++;
		}
		this.cdr.markForCheck();
	}

	get products(): string | (ProductResponse | CallToActionResponse)[] {
		return this._products;
	}

	get isOutOfStockEnabled(): boolean {
		return (
			this.target === SearchTarget.Search ||
			this.target === SearchTarget.Browse ||
			[ListType.PastOrders, ListType.SavedList, ListType.Favourites].includes(this.listtype)
		);
	}

	shouldHideOutOfStockProduct(product: ProductResponse | CallToActionResponse): boolean {
		return this.isProductResponse(product)
			? isProductOutOfStock(product as ProductResponse) && !this.isOutOfStockEnabled
			: false;
	}

	ngOnInit(): void {
		this._isCartologyGA4EventTrackingEnabled = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.EnableCartologyGA4EventTracking
		);

		this.isCartologyGAMSessionGroup = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.CartologyGAMSessionGroup
		);

		if (this.isCartologyGAMSessionGroup && this.window) {
			this.window.cartologyGAMSessionGroup = true;
			this.window.addEventListener(
				'promo-element-ready-for-default-impression-observability',
				this.defaultImpressionEventHandler
			);
		} else {
			this.window.cartologyGAMSessionGroup = false;
		}

		this.shopperState$.subscribe((shopper: ShopperState) => {
			this.isshopper = shopper.isShopper || false;
			this.isloggedin = shopper.isLoggedIn || false;
			this.cdr.markForCheck();
		});

		this.productImpressionArgs = this.getProductImpressionArgs();
	}

	ngOnDestroy(): void {
		if (this.isCartologyGAMSessionGroup && this.window) {
			this.window.removeEventListener(
				'promo-element-ready-for-default-impression-observability',
				this.defaultImpressionEventHandler
			);
		}
	}

	showBoughtBefore(position: number): boolean {
		return position <= this.maxBoughtBeforePosition;
	}

	handleImpression(cta: CallToActionResponse, additionalClassNames = ''): void {
		if (!cta.positionIndex) {
			return;
		}

		// No need to add one to positionIndex as it starts with 1 in CTA
		this.datalayerService.trackPromoImpression({
			id: cta.id,
			name: cta.name,
			position: `${cta.positionIndex}`,
			creative: 'Content grid tile',
		});

		if (!(this.isCartologyGAMSessionGroup && additionalClassNames?.includes('cartology-gam-ad'))) {
			this.tealiumUtagService.view({
				tealium_event: TealiumEventEnum.ViewPromotion,
				ecommerce: {
					creative_name: `${this._searchQuery !== undefined ? 'Search' : 'Browse'} CIG Tile`,
					creative_slot: cta.positionIndex,
					promotion_id: extractQLCodeFromCtaName(cta.name) ?? cta.id,
					promotion_name: cta.name,
				},
			});
		} else {
			this._cartologyGAMCallToAction = cta;
		}
	}

	handleClick(cta: CallToActionResponse): void {
		if (!cta.positionIndex) {
			return;
		}

		this.datalayerService.trackPromoClick({
			id: cta.id,
			name: cta.name,
			position: `${cta.positionIndex}`,
			creative: 'Content grid tile',
		});

		if (this._isCartologyGA4EventTrackingEnabled) {
			this.tealiumUtagService.link({
				tealium_event: TealiumEventEnum.SelectPromotion,
				ecommerce: {
					creative_name: `${this._searchQuery !== undefined ? 'Search' : 'Browse'} CIG Tile`,
					creative_slot: cta.positionIndex,
					promotion_id: extractQLCodeFromCtaName(cta.name) ?? cta.id,
					promotion_name: cta.name,
				},
			});
		}
	}

	handleCppClicked(item: CallToActionResponse | ProductResponse, index: number): void {
		const productType = getProductType(this.isContentInGridCarousel(item), (item as ProductResponse).adId);
		const currentUrl = this.appSettingsService.getSetting('currentUrl');
		const listType = this.datalayerService.getListTypeByUrl(currentUrl);
		const creativeSlot = index.toString();
		if (
			this._isCartologyGA4EventTrackingEnabled &&
			'sku' in item &&
			productType === ProductType.CPP &&
			listType === 'Browse'
		) {
			this.tealiumUtagService.link({
				tealium_event: TealiumEventEnum.SelectPromotion,
				ecommerce: {
					creative_name: this.creativeName,
					creative_slot: creativeSlot,
					promotion_id: item.sku,
					promotion_name: item.name,
					items: [
						{
							item_id: item.sku,
							item_name: item.name,
							index: index,
							item_brand: item.brand,
							item_category: item.departments?.[0]?.name ?? '',
							item_category2: item.departments?.[1]?.name ?? '',
							item_category3: item.departments?.[2]?.name ?? '',
							item_list_id: '',
							item_list_name: `${this._searchQuery !== undefined ? 'Search' : 'Browse'}`,
							item_variant: productType,
						},
					],
				},
			});
		}
	}
	handleImpressionContentInGridCarousel(content: PromotionalCarouselResponseModel): void {
		if (!content.positionIndex) {
			return;
		}

		this.datalayerService.trackPromoImpression({
			id: content.id,
			name: content.name,
			position: `${content.positionIndex}`,
			creative: 'Content grid carousel tile',
		});

		const gridPostion = this.gridPositions[this.gridCarouselIndex];
		const products = content.products.map(
			(product, index) =>
				({
					...product,
					position: `${gridPostion}.${index + 1}`,
					productType: ProductType.ProductInGrid,
				}) as ProductImpressionData
		);
		this.datalayerService.trackProductImpressions(products, content.name);
		this.tealiumUtagService.trackViewItemList(products);
	}

	handleClickContentInGridCarousel(content: PromotionalCarouselResponseModel): void {
		if (!content.positionIndex) {
			return;
		}

		this.datalayerService.trackPromoClick({
			id: content.id,
			name: content.name,
			position: `${content.positionIndex}`,
			creative: 'Content grid carousel tile',
		});
	}

	isProductVisible(sku: string): boolean | undefined {
		if (this._productsVisibility.get(sku) === undefined) {
			return true;
		}

		return this._productsVisibility.get(sku);
	}

	handleVisibleOnChange(sku: string, isVisible: boolean): void {
		if (!sku) {
			return;
		}

		this._productsVisibility.set(sku, isVisible);
	}

	/**
	 * For a boost offer: Returns whether a boost offer is activated
	 * Otherwise returns false
	 */
	isBoostOfferActivated(productTag: ProductTag | undefined | null): Observable<boolean> {
		const boostOfferId = this.getBoostOfferId(productTag);
		return boostOfferId ? this.boostsFacade.isBoostOfferActivated(boostOfferId) : of(false);
	}

	private readonly defaultImpressionEventHandler = (event: Event): void => {
		const customEvent = event as CustomEvent;
		const eventElement = customEvent.detail?.element;
		if (
			this._cartologyGAMCallToAction &&
			(!eventElement ||
				eventElement?.id === 'cartology-favourite-cig-tile' ||
				eventElement?.id === 'cartology-product-cig-tile')
		) {
			this.tealiumUtagService.view({
				tealium_event: TealiumEventEnum.ViewPromotion,
				ecommerce: {
					creative_name: `${this._searchQuery !== undefined ? 'Search' : 'Browse'} CIG Tile`,
					creative_slot: this._cartologyGAMCallToAction.positionIndex,
					promotion_id:
						extractQLCodeFromCtaName(this._cartologyGAMCallToAction.name) ??
						this._cartologyGAMCallToAction.id,
					promotion_name: this._cartologyGAMCallToAction.name,
				},
			});
		}
	};

	private getTrackingId(product: any): string {
		if (
			this.isCartologyGAMSessionGroup === true &&
			product?.type === 'PromoTile' &&
			product.additionalClassNames &&
			product.additionalClassNames.includes('cartology-gam-ad')
		) {
			return `${(product as CallToActionResponse)?.id}-${this._contentInGridCarouselSuffix}`;
		}

		return `${product.sku}-${product.selectedPurchasingUnit}`;
	}

	private getBoostOfferId(productTag: ProductTag | undefined | null): string | undefined {
		return productTag?.boostOffer?.offerId;
	}

	private getViewItemData(params: ViewItemParams): ViewItemListArgs {
		if (!this.isProductResponse(params.item)) {
			return this.buildEmptyViewItemData(params);
		}

		const itemDetails = this.buildItemDetails(params);

		return {
			pageItemListId: params.itemPageItemListName ?? VIEW_ITEM_DATA_NOT_APPLICABLE,
			pageItemListName: params.itemListName,
			items: [itemDetails],
			carouselName: this.name,
		};
	}

	private buildEmptyViewItemData(params: ViewItemParams): ViewItemListArgs {
		return {
			pageItemListId: params.itemPageItemListName ?? VIEW_ITEM_DATA_NOT_APPLICABLE,
			pageItemListName: params.itemListName,
			items: [],
		};
	}

	private getItemListId(item: any): string {
		return item.onlineSample?.campaignId ? item.onlineSample.campaignId.toString() : VIEW_ITEM_DATA_NOT_APPLICABLE;
	}

	private getItemListName(item: any): string {
		if (!item.brand) {
			return VIEW_ITEM_DATA_NOT_APPLICABLE;
		}
		return item.brand.toString().toLowerCase() === VIEW_ITEM_ONLINE_SAMPLE_BRAND
			? VIEW_ITEM_ONLINE_SAMPLE_LIST_NAME
			: item.onlineSample?.campaignName?.trim() || VIEW_ITEM_DATA_NOT_APPLICABLE;
	}

	private buildItemDetails(params: ViewItemParams): any {
		const item = params.item;

		return {
			itemId: item.sku ?? '',
			itemName: item.name ?? '',
			index: this.productIndexOffset + this.gridPositions[params.index],
			promotionProductTags: item.productTag?.tagType ? [item.productTag.tagType] : [],
			discount: getDiscountDiffValue(item?.price?.originalPrice, item?.price?.salePrice),
			itemBrand: item.brand ?? '',
			itemCategory: item.departments?.[0]?.name ?? VIEW_ITEM_DATA_NOT_APPLICABLE,
			itemCategory2: item.departments?.[1]?.name ?? VIEW_ITEM_DATA_NOT_APPLICABLE,
			itemCategory3: item.departments?.[2]?.name ?? VIEW_ITEM_DATA_NOT_APPLICABLE,
			itemListId: this.getItemListId(item),
			itemListName: this.getItemListName(item),
			itemVariant: params.productType,
			price: item.price?.salePrice,
			hasShopperNotes: item.hasShopperNotes,
			availabilityStatus: item.availabilityStatus ?? VIEW_ITEM_DATA_NOT_APPLICABLE,
			filterTypes: [params.sortData, ...(params.filterData ?? [''])],
			eachKg: item.unit ?? VIEW_ITEM_DATA_NOT_APPLICABLE,
		};
	}

	private getViewPromotionCPP(
		item: any,
		index: number,
		listType: string | undefined,
		productType: string
	): PromoCPPImpressionArgs | undefined {
		if (!this.shouldTrackPromotion(item, listType, productType)) {
			return undefined;
		}
		return this.buildPromotionArgs(item, index, listType, productType);
	}

	private shouldTrackPromotion(item: any, listType: string | undefined, productType: string): boolean {
		return (
			this._isCartologyGA4EventTrackingEnabled &&
			this.isProductResponse(item) &&
			(listType === 'HYF' ||
				(listType?.toLocaleLowerCase() === SearchTarget.Browse && productType === ProductType.CPP))
		);
	}

	private buildPromotionArgs(
		item: any,
		index: number,
		listType: string | undefined,
		productType: string
	): PromoCPPImpressionArgs {
		const newCreativeName = listType === 'HYF' ? (this.name ?? '') : (this.creativeName ?? '');
		const newPromotionName = item.onlineSample?.campaignName?.trim() || item.name || '';
		const newPromotionId = item.onlineSample?.campaignId?.toString() || item.sku || '';

		return {
			creativeName: newCreativeName,
			creativeSlot: (this.productIndexOffset + this.gridPositions[index]).toString(),
			promotionId: newPromotionId,
			promotionName: newPromotionName,
			items: [this.buildPromotionItem(item, index, listType, productType)],
		};
	}

	private buildPromotionItem(item: any, index: number, listType: string | undefined, productType: string): any {
		return {
			itemId: item.sku ?? '',
			itemName: item.name ?? '',
			index: this.productIndexOffset + this.gridPositions[index],
			itemBrand: item.brand ?? '',
			itemCategory: item.departments?.[0]?.name ?? '',
			itemCategory2: item.departments?.[1]?.name ?? '',
			itemCategory3: item.departments?.[2]?.name ?? '',
			itemListId: '',
			itemListName: listType,
			itemVariant: productType ?? '',
		};
	}

	private getProductImpressionArgs(): ProductImpressionAndViewItemListArgs[] {
		const products = this._products;
		const carouselName = this.name;
		const itemPageItemListName = this.listName;
		const sortData = this.searchSortOptions?.filter((i) => i.selected)[0].text ?? this.baseSortOptions ?? '';
		const filterData = this.selectedFilters?.map((i) => i.name ?? '');

		const gtmListName = this.loggingService.getRouteData(this.route.snapshot).gtmListName ?? '';
		const itemListName = carouselName ? carouselName : gtmListName;
		const currentUrl = this.appSettingsService.getSetting('currentUrl');
		const listType = this.datalayerService.getListTypeByUrl(currentUrl);

		if (products) {
			return products.map((item, index) => {
				let productResponse: ProductResponse;
				if (this.isProductResponse(item) || this.isContentInGridCarousel(item)) {
					productResponse = item;
				} else {
					productResponse = { sku: `${item.id}`, name: item.name };
				}

				const productType = getProductType(this.isContentInGridCarousel(item), (item as ProductResponse).adId);

				const impressionData: ProductImpression = {
					product: {
						...productResponse,
						position: `${this.productIndexOffset + this.gridPositions[index]}`,
						productType: productType,
						searchValue: this._searchQuery,
						searchResultsCount: this._totalItemsCount,
					},
					carouselName,
				};

				const viewItemData = this.getViewItemData({
					item,
					index,
					itemPageItemListName,
					itemListName,
					sortData,
					filterData,
					productType,
				});

				const viewPromotionCPP = this.getViewPromotionCPP(item, index, listType, productType);

				return {
					productImpressionList: impressionData,
					viewItemList: viewItemData,
					viewPromotionCPP: viewPromotionCPP,
				};
			});
		} else {
			return [];
		}
	}

	private isProductResponse(response: CallToActionResponse | ProductResponse): response is ProductResponse {
		return (response as ProductResponse).type === 'Product';
	}

	private isContentInGridCarousel(response: CallToActionResponse | ProductResponse): boolean {
		return (response as ProductResponse).type === 'PromotionalCarousel';
	}
}
